import { Form, Modal } from 'react-bootstrap-5';
import React, { useState, useEffect, useMemo } from 'react';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';

import { post, put } from 'helpers/api';
import ErrorAlert from '../../project/components/new_listing_modal/error_alert';
import Button from 'shared/button';
import StateSelect from 'components/state_select';
import { countries } from 'helpers/countries';
import { type Territory } from './index';

const UNITED_STATES_COUNTRY_ID = 1;
const USA_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '400px'
};

const MAP_OPTIONS: google.maps.MapOptions = {
  clickableIcons: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  styles: [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }],
    }
  ]
};

const POLYGON_OPTIONS: google.maps.PolygonOptions = {
  fillColor: '#2196F3',
  fillOpacity: 0.35,
  strokeColor: '#1976D2',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: false,
  geodesic: false,
  zIndex: 1
};

interface EditModalProps {
  territory: Territory | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (territory: Territory) => void;
  googleMapsApiKey?: string;
}

export default function TerritoryEditModal({
  territory,
  isOpen,
  onClose,
  onSuccess,
  googleMapsApiKey = ''
}: EditModalProps) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey
  });

  const [formFields, setFormFields] = useState({
    label: territory?.label || '',
    state: territory?.state || '',
    county: territory?.county || '',
    countryId: territory?.countryId || UNITED_STATES_COUNTRY_ID
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormFields({
        state: territory?.state || '',
        county: territory?.county || '',
        countryId: territory?.countryId || UNITED_STATES_COUNTRY_ID,
        label: territory?.label || ''
      });
      setErrors([]);
    }
  }, [isOpen, territory]);

  const isEditing = !!territory?.id;
  const showMap = isEditing && territory?.boundary && googleMapsApiKey;

  function formFieldChanged(fieldName: string, fieldValue: string | number) {
    setFormFields({ ...formFields, [fieldName]: fieldValue });
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();

    setIsSubmitting(true);
    setErrors([]);

    try {
      const url = isEditing ? `/territories/${territory.id}` : '/territories';
      const fetchMethod = isEditing ? put : post;

      // Reset state and county to empty string if non-US country is selected
      const territoryData = formFields.countryId === UNITED_STATES_COUNTRY_ID
        ? formFields
        : { ...formFields, state: '', county: '' };

      const res = await fetchMethod(url, { territory: territoryData });

      setIsSubmitting(false);
      if (res.id) {
        onSuccess(res);
      } else {
        setErrors(res);
      }
    } catch {
      setIsSubmitting(false);
      const uploadType = isEditing ? 'updated' : 'created';
      setErrors([`Territory could not be ${uploadType}. Please try again or contact support for help.`]);
    }
  }

  const polygonPaths = useMemo(() => {
    if (!territory?.boundary?.coordinates) return [];

    const { type, coordinates } = territory.boundary;

    if (type === 'Polygon') {
      return coordinates.map(ring => ring.map(([lng, lat]) => ({ lat, lng }))
      );
    } else if (type === 'MultiPolygon') {
      if (coordinates.length > 0) {
        return coordinates[0].map(ring => ring.map(([lng, lat]) => ({ lat, lng }))
        );
      }
    }

    return [];
  }, [territory?.boundary]);

  useEffect(() => {
    if (!map || !polygonPaths.length || polygonPaths[0].length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    polygonPaths[0].forEach((point) => {
      bounds.extend(point);
    });

    map.fitBounds(bounds);
  }, [map, polygonPaths]);

  const handleMapLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
    >
      <Modal.Body className="p-5">
        <h5 className="fw-light text-center mt-0 mb-4">
          {isEditing ? 'Edit' : 'Add a'} Territory
        </h5>
        <ErrorAlert errorMessages={errors} hasErrors={errors.length > 0}/>
        <form onSubmit={submitForm}>
          <div className="form-vertical-field-container">
            <Form.Group className="form-group">
              <Form.Label htmlFor="territory-country">Country</Form.Label>
              <Form.Select
                id="territory-country"
                value={formFields.countryId}
                onChange={e => formFieldChanged('countryId', Number(e.target.value))}
              >
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {formFields.countryId === UNITED_STATES_COUNTRY_ID && (
              <>
                <Form.Group className="form-group">
                  <Form.Label htmlFor="territory-state">State</Form.Label>
                  <StateSelect
                    className="form-control"
                    id="territory-state"
                    value={formFields.state}
                    onChange={e => formFieldChanged('state', e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="form-group">
                  <Form.Label htmlFor="territory-county">County</Form.Label>
                  <Form.Control
                    id="territory-county"
                    placeholder="Enter Territory County"
                    type="text"
                    value={formFields.county}
                    onChange={e => formFieldChanged('county', e.target.value)}
                  />
                </Form.Group>
              </>
            )}

            <Form.Group className="form-group">
              <Form.Label htmlFor="territory-label">Territory / Label</Form.Label>
              <Form.Control
                id="territory-label"
                placeholder="Territory Label"
                type="text"
                value={formFields.label}
                onChange={e => formFieldChanged('label', e.target.value)}
              />
              <Form.Text className="text-muted mt-1 mb-2">
                {formFields.countryId === UNITED_STATES_COUNTRY_ID
                  ? 'If different than county'
                  : 'If different than the country name'}
              </Form.Text>
            </Form.Group>

            {showMap && isLoaded && (
              <Form.Group className="form-group">
                <Form.Label>Territory Boundary</Form.Label>
                <GoogleMap
                  center={USA_CENTER}
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  options={MAP_OPTIONS}
                  zoom={DEFAULT_ZOOM}
                  onLoad={handleMapLoad}
                >
                  {polygonPaths.length > 0 && (
                    <Polygon
                      options={POLYGON_OPTIONS}
                      paths={polygonPaths}
                    />
                  )}
                </GoogleMap>
              </Form.Group>
            )}

            {
              !showMap && isEditing && (
                <Form.Label>Territory boundary not found</Form.Label>
              )
            }
          </div>

          <div className="d-flex justify-content-end gap-3 mt-7">
            <Button.Cancel
              disabled={isSubmitting}
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button.Cancel>
            <Button.Primary disabled={isSubmitting} type="submit">
              {isEditing ? 'Update' : 'Add'} Territory
            </Button.Primary>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
